import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { number, object, func, string } from "prop-types";
import get from "lodash/get";

import Button from "../../atoms/button";
import CheckBox from "../../atoms/checkbox";
import PopupModal from "../../atoms/popup-modal";
import PaymentFailure from "../../molecules/payment-failure";
import SubscribePlans from "../../molecules/subscribe-plans";
import SupportDescriptionView from "../../molecules/support-description-view";
import MobileNumberUpdateView from "../../molecules/mobile-number-update-view";
import PayButtonView from "../../molecules/pay-button-view";
import LoginPay from "../../rows/login-pay";
import { readCookie, eraseCookie } from "../../../../isomorphic/components/helper/cookies";

import { setLoading } from "../../helper/actions";
import { isMobileNoPresent, addGTMPageVariables } from "../../utils/utils";

import { getAllPlans } from "../../helper/access-type";

import "./planSelection.m.css";

const PlanSelectionPageView = ({ recommendedId, member, setLoading }) => {
  const [allPlans, setAllPlans] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const [recurringPlans, setRecurringPlans] = useState([]);
  const [nonRecurringPlans, setNonRecurringPlans] = useState([]);
  const [isRecurringChecked, setIsRecurringChecked] = useState(true);
  const [showRecurringCheckBox, setRecurringCheckBox] = useState(false);

  useEffect(() => {
    let initiatePage = window.localStorage.getItem("sym_initiate_page") || "Direct Landing";
    let initiatePosition = window.localStorage.getItem("sym_initiate_position") || "Direct";

    window.localStorage.removeItem("sym_initiate_page");
    window.localStorage.removeItem("sym_initiate_position");

    if (window.location.search) {
      const params = new URLSearchParams(window.location.search);
      initiatePage = `${params.get("utm_source") || ""} - ${params.get("utm_content") || ""}`;
      initiatePosition = `${params.get("utm_campaign") || ""} - ${params.get("utm_medium") || ""}`;
    }

    addGTMPageVariables(null, "WEB", null, "Plan Selection");

    window.dataLayer.push({
      event: "support_thequint_sym",
      sym_initiate_page: initiatePage,
      sym_initiate_position: initiatePosition,
    });

    window.localStorage.setItem("plan_initiate_page", initiatePage);
    window.localStorage.setItem("plan_initiate_position", initiatePosition);
  }, []);

  useEffect(() => {
    if (global.AccessType) {
      getAllPlans().then((plans) => filterPlans(plans));
    }
  }, []);

  useEffect(() => {
    if (readCookie("status") === "failure") {
      eraseCookie("status");
      setShowPopup(true);
    }
  }, []);

  const handlePlanSelect = (index) => {
    setSelectedIndex(index);
    setIsRecurringChecked(true);
    if (nonRecurringPlans.map((plan) => plan.title).includes(recurringPlans[index].title)) {
      setRecurringCheckBox(true);
    } else {
      setRecurringCheckBox(false);
    }
  };

  const filterPlans = (plans) => {
    setAllPlans(plans);
    const sortedPlans = plans
      .filter((i) => i.price_cents && i.subscription_group_id !== 186)
      .sort((planA, planB) => planA.id - planB.id);
    const recurring = sortedPlans.filter((plan) => plan.recurring);
    const nonRecurring = sortedPlans.filter((plan) => !plan.recurring);

    setRecurringPlans(recurring);
    setNonRecurringPlans(nonRecurring);
  };

  const getPaySupportView = () => {
    // If mobile number not available when user is logged in then request user to update it
    if (!isMobileNoPresent(member)) {
      return <MobileNumberUpdateView member={member} setLoading={setLoading} />;
    }

    let planObj = null;

    if (isRecurringChecked) {
      planObj = recurringPlans[selectedIndex];
    } else {
      if (!allPlans.length) return null;
      const planName = recurringPlans[selectedIndex].title;
      planObj = nonRecurringPlans.find((plan) => plan.title === planName);
    }

    return <PayButtonView member={member} setLoading={setLoading} plans={allPlans} plan={planObj} />;
  };

  const getRecurringPaymentView = () => {
    const style = showRecurringCheckBox ? "recurring-open" : "recurring-hide";

    return (
      <div styleName={`recurring-checkbox-wrapper ${style}`}>
        <CheckBox
          isChecked={isRecurringChecked}
          type="type2"
          text={isRecurringChecked ? "Uncheck to make a one-time payment" : "Check to make a recurring payment"}
          onChange={(e) => setIsRecurringChecked(e.target.checked)}
        />
      </div>
    );
  };

  const faqClick = () => window.open("/membership/faq", "_blank");

  return (
    <div styleName="wrapper">
      <span styleName="support-quint-title">Support The Quint.</span>
      <span styleName="support-quint-description">Be a part of responsible journalism.</span>
      <SubscribePlans
        selectedIndex={selectedIndex}
        setSelectedIndex={handlePlanSelect}
        plans={recurringPlans}
        recommendedId={recommendedId}
      />
      <span styleName="message1" className="container">
        {isRecurringChecked
          ? "This is a seamless, recurring payment plan. You can cancel your membership anytime from the profile page."
          : "This is a seamless payment plan. You can cancel your membership anytime from the profile page."}
      </span>
      {getRecurringPaymentView()}

      {member ? getPaySupportView() : <LoginPay />}

      <div styleName="faq-wrapper">
        <span styleName="faq-title">Frequently Asked Questions</span>
        <Button content="Click Here" buttonType="type2" onClick={faqClick} />
      </div>

      <SupportDescriptionView />

      <iframe
        styleName="video-wrapper"
        src="https://www.youtube.com/embed/GmWidole6MI"
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen=""
      ></iframe>

      {showPopup ? (
        <PopupModal closePopup={() => setShowPopup(false)}>
          <PaymentFailure />
        </PopupModal>
      ) : null}
    </div>
  );
};

PlanSelectionPageView.propTypes = {
  recommendedId: number,
  member: object,
  setLoading: func,
  navigateToPage: func,
  publisherName: string,
};

const mapStateToProps = (state) => {
  const config = get(state, ["qt", "config", "publisher-attributes"]);

  return {
    recommendedId: config.accesstype.recommended,
    publisherName: config.name,
    member: state.member,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
});

const PlanSelectionPage = connect(mapStateToProps, mapDispatchToProps)(PlanSelectionPageView);

export { PlanSelectionPage };
